import React, { useState, useRef, useEffect } from "react";
import { Box, TextField, IconButton, Button, Typography, Menu, MenuItem, Paper, Chip, ClickAwayListener, Divider } from "@mui/material";
import { InsertEmoticon, AttachFile, MoreVert } from "@mui/icons-material";
import Picker from "emoji-picker-react";
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime';
import { useDispatch, useSelector } from "react-redux";
import { discussionOrderCreate, discussionOrderEdit, discussionScoop } from "../../redux/Thunk/DashboardModule/DashboardThunk";

dayjs.extend(relativeTime);

const convertTextToEmoji = (text) => {
  const emojiMap = {
    ':)': '🙂',
    ':-)': '🙂',
    ':(': '🙁',
    ':-(': '🙁',
    ':D': '😄',
    ':-D': '😄',
    ';)': '😉',
    ';-)': '😉',
    '<3': '❤️',
  };

  let convertedText = text;

  Object.keys(emojiMap).forEach((key) => {
    const escapedKey = key.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');
    convertedText = convertedText.replace(
      new RegExp(escapedKey, 'g'),
      emojiMap[key]
    );
  });

  return convertedText;
};


const ChatWindow = ({ data, userDetails, tabValue, selectedJobOrItemIdConditiionally, showFromFreelance = false }) => {
    const { user } = useSelector((state) => state?.root?.authUser);
    const { dropdowns } = useSelector((state) => state?.root?.DashboardModule);
    const [messages, setMessages] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMsgIndex, setSelectedMsgIndex] = useState(null);
    const [text, setText] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [queuedFile, setQueuedFile] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editMsgIndex, setEditMsgIndex] = useState(null);
    const [isCurrentUserMessage, setIsCurrentUserMessage] = useState(false);
    const fileInputRef = useRef(null);
    const [pingedUserIds, setPingedUserIds] = useState([]);
    const [mentionAnchorEl, setMentionAnchorEl] = useState(null);
    const [mentionQuery, setMentionQuery] = useState('');
    const [pingedUsernames, setPingedUsernames] = useState([]); // Track cleanly

    const { users_list } = useSelector((state) => state?.root?.InvoiceModule);
    const dynamicStateDropdown = user?.iFkUserTypeId === 2 ? dropdowns?.internalUser : users_list?.data;
    const messagesEndRef = useRef(null);
    const dispatch = useDispatch();

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result); // Keep the full data URI
            reader.onerror = (error) => reject(error);
        });
    };

    // Initialize messages from props
    useEffect(() => {
        if (data) {
            setMessages(data.map(msg => {
                // Parse attachments if they exist
                let attachments = [];
                if (msg.attachments && msg.attachments !== "") {
                    try {
                        attachments = JSON.parse(msg.attachments);
                    } catch (e) {
                        console.error("Error parsing attachments:", e);
                    }
                }

                return {
                    ...msg,
                    time: dayjs(msg.created),
                    attachments: attachments
                };
            }));
        }
    }, [data]);
    const handleSend = async () => {
        if (
            !text.trim() &&
            queuedFile.length === 0 &&
            (!isEditing || !messages[editMsgIndex]?.attachments?.length)
        ) {
            return;
        }

        // Editing existing message
        if (isEditing && editMsgIndex !== null) {
            const msgToEdit = messages[editMsgIndex];
            let attachments = [...(msgToEdit.attachments || [])];

            // Add new files
            for (const file of queuedFile) {
                const base64Data = await fileToBase64(file);
                attachments.push({
                    original_filename: file.name,
                    mime_type: file.type || 'application/octet-stream',
                    file: base64Data,
                });
            }

            const payload = {
                content: convertTextToEmoji(text.trim()),
                login_userid: user?.iUserId,
                attachments: attachments,
            };

            dispatch(discussionOrderEdit({ id: msgToEdit?.id, payload }));

            setMessages(prev =>
                prev.map((msg, idx) =>
                    idx === editMsgIndex ? {
                        ...msg,
                        content: convertTextToEmoji(text.trim()),
                        modified: new Date().toISOString(),
                        attachments: attachments,
                    } : msg
                )
            );

            // Reset states
            setIsEditing(false);
            setEditMsgIndex(null);
            setText("");
            setQueuedFile([]);
            return;
        }

        // Creating new message
        let attachments = [];

        for (const file of queuedFile) {
            const base64Data = await fileToBase64(file);
            attachments.push({
                original_filename: file.name,
                mime_type: file.type || 'application/octet-stream',
                file: base64Data,
            });
        }

        const newMessage = {
            created: new Date().toISOString(),
            modified: new Date().toISOString(),
            //content: text.trim(),
            content: convertTextToEmoji(text.trim()),
            pings: pingedUserIds.join(","), // ✅ Clean comma-separated pings
            fullname: user?.vUserName || "User",
            user_id: user?.iUserId,
            profile_picture_url: user?.vProfilePic || "uploads/profilePic/default.jpg",
            created_by_current_user: true,
            upvote_count: 0,
            user_has_upvoted: false,
            externalChat: tabValue === 1 ? 1 : 0,
            order_id: userDetails?.order_id || 0,
            scoop_id: userDetails?.itemId || 0,
            job_id: tabValue === 1 ? selectedJobOrItemIdConditiionally : 0,
            isCommentEmailsend: (showFromFreelance || tabValue === 1) ? true : false,
            read_id: user?.iUserId,
        };

        const payload = {
            ...newMessage,
            attachments: attachments,
        };

        dispatch(discussionOrderCreate(payload)).then(() => {
            const payload = {
                id: selectedJobOrItemIdConditiionally,
                type: tabValue === 0 ? 1 : 2,
            }
            dispatch(discussionScoop(payload));
        });

        setMessages((prev) => [...prev, {
            ...newMessage,
            id: Date.now(), // Temporary ID until real one comes from API
            attachments: attachments,
        }]);

        // Reset input
        setText("");
        setQueuedFile([]);
        setPingedUsernames([]);
        setPingedUserIds([]);
        setShowEmojiPicker(false);
    };


    const handleEmojiClick = (emojiData) => {
        setText((prev) => prev + emojiData.emoji);
    };

    const handleClose = () => setAnchorEl(null);

    const handleDelete = () => {
        const messageToDelete = messages[selectedMsgIndex];
        const payload = {
            is_deleted: 1,
            login_userid: user?.iUserId,
        };

        if (messageToDelete?.id) {
            dispatch(discussionOrderEdit({ id: messageToDelete.id, payload }));
        }

        setMessages((prev) => prev.filter((_, i) => i !== selectedMsgIndex));
        handleClose();
    };

    const handleCopy = () => {
        const msg = messages[selectedMsgIndex];
        navigator.clipboard.writeText(msg.content || msg.attachments?.[0]?.original_filename || "");
        handleClose();
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setQueuedFile((prev) => [...prev, ...files]);
        }
        e.target.value = null;
    };


    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const formatDateDivider = (date) => {
        const today = dayjs().startOf('day');
        const messageDate = dayjs(date).startOf('day');

        if (messageDate.isSame(today, 'day')) {
            return 'Today';
        } else if (messageDate.isSame(today.subtract(1, 'day'), 'day')) {
            return 'Yesterday';
        } else if (messageDate.isAfter(today.subtract(7, 'day'))) {
            return messageDate.format('dddd');
        } else {
            return messageDate.format('DD/MM/YYYY');
        }
    };

    const shouldShowDateDivider = (index) => {
        if (index === 0) return true;
        const currentDate = dayjs(messages[index].time).startOf('day');
        const prevDate = dayjs(messages[index - 1].time).startOf('day');
        return !currentDate.isSame(prevDate, 'day');
    };

    const handleDownload = async (attachment) => {
        try {
            const response = await fetch(attachment.file || '');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.original_filename || 'downloaded-file.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url); // Cleanup
        } catch (error) {
            console.error("Error downloading file:", error);
        }
    };


    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", px: 2, pt: 1, position: "relative" }}>
            {/* Chat Area */}
            <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
                {messages?.map((msg, i) => (
                    <React.Fragment key={msg.id || i}>
                        {shouldShowDateDivider(i) && (
                            <Divider sx={{ my: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {formatDateDivider(msg.time)}
                                </Typography>
                            </Divider>
                        )}
                        {msg.user_id !== user?.iUserId && <Typography variant="caption" color="text.secondary">
                            {msg.fullname}
                        </Typography>}
                        {/* Message Paper Component */}
                        <Paper className="message-paper"
                            sx={{
                                p: 1,
                                mb: 1,
                                pr: 4,
                                color: msg.user_id === user?.iUserId ? "#fff" : "#000",
                                borderRadius: msg.user_id === user?.iUserId ? "0.75rem 0.75rem 0px 0.75rem" : "0.75rem 0.75rem 0.75rem 0px",
                                backgroundColor: msg.user_id === user?.iUserId ? "rgba(78, 47, 196, 1)" : "rgba(78, 47, 196, 0.15)",
                                maxWidth: "70%",
                                width: "fit-content",
                                minWidth: "100px",
                                ml: msg.user_id === user?.iUserId ? "auto" : 0,
                                mr: msg.user_id !== user?.iUserId ? "auto" : 0,
                                position: "relative",
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                                '&:hover .message-actions': {
                                    visibility: 'visible',
                                    opacity: 1
                                }
                            }}
                        >
                            {/* Message content */}
                            {msg.content && (
                                <Typography sx={{ maxWidth: "100%", overflowWrap: "break-word" }}>
                                    {msg.content}
                                </Typography>
                            )}

                            {/* Attachments */}
                            {msg.attachments?.length > 0 && (
                                <Box mt={1} sx={{ maxWidth: "100%" }}>
                                    {msg.attachments.map((attachment, idx) => (
                                        <Typography
                                            key={idx}
                                            variant="body2"
                                            sx={{
                                                overflowWrap: "break-word",
                                                cursor: "pointer",
                                                '&:hover': {
                                                    textDecoration: "underline"
                                                }
                                            }}
                                            onClick={() => handleDownload(attachment)}
                                        >
                                            📎 {attachment.original_filename}
                                        </Typography>
                                    ))}
                                </Box>
                            )}

                            {/* Timestamp */}
                            <Typography variant="caption" sx={{ display: "block", textAlign: "right", mt: 0.5 }}>
                                {dayjs(msg.created).format("h:mm A")}
                            </Typography>

                            {/* 3-dot menu (right side for all messages) */}
                            <IconButton
                                className="message-actions"
                                size="small"
                                sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    color: msg.user_id === user?.iUserId ? "#fff" : "#000",
                                    visibility: msg.user_id === user?.iUserId ? 'visible' : 'hidden',
                                    // opacity: msg.user_id === user?.iUserId ? 1 : 0,
                                    // transition: 'opacity 0.2s ease',
                                }}
                                onClick={(e) => {
                                    setSelectedMsgIndex(i);
                                    setAnchorEl(e.currentTarget);
                                    setIsCurrentUserMessage(msg.user_id === user?.iUserId);
                                }}
                            >
                                <MoreVert fontSize="small" />
                            </IconButton>
                        </Paper>

                    </React.Fragment>
                ))}
                {/* Message Options Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                >
                    <MenuItem onClick={handleCopy}>Copy</MenuItem>
                    {isCurrentUserMessage && (
                        <>
                            <MenuItem onClick={handleDelete}>Delete</MenuItem>
                            <MenuItem onClick={() => {
                                const msgToEdit = messages[selectedMsgIndex];
                                setText(msgToEdit?.content || "");
                                setQueuedFile([]);
                                setIsEditing(true);
                                setEditMsgIndex(selectedMsgIndex);
                                handleClose();
                            }}>
                                Edit
                            </MenuItem>
                        </>
                    )}
                </Menu>
                <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1,
                    pt: 1,
                    borderTop: "1px solid #ddd",
                }}
            >
                <IconButton sx={{ mb: 2 }} onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                    <InsertEmoticon />
                </IconButton>

                {!showFromFreelance && <IconButton sx={{ mb: 2 }} component="label">
                    <AttachFile />
                    <input
                        hidden
                        type="file"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                    />
                </IconButton>}

                <Box sx={{ flexGrow: 1, position: "relative" }}>
                    <TextField
                        multiline
                        maxRows={2}
                        fullWidth
                        placeholder="Type a message"
                        value={text}
                        onChange={(e) => {
                            const cursorPos = e.target.selectionStart;
                            const value = e.target.value;
                            setText(value);

                            // Detect active mention being typed (after @)
                            const beforeCursor = value.slice(0, cursorPos);
                            const match = /@([^\s@]*)$/.exec(beforeCursor);

                            if (match) {
                                setMentionQuery(match[1]);
                                setMentionAnchorEl(e.currentTarget);
                            } else {
                                setMentionAnchorEl(null);
                                setMentionQuery('');
                            }

                            // Detect all complete @mentions in the text
                            const currentMentions = [...value.matchAll(/@([^\n@]+)/g)].map(m => m[1].trim());

                            // Match mentions to users in the list
                            const activeUsers = dynamicStateDropdown?.filter(user =>
                                currentMentions.includes(user.vUserName)
                            ) || [];

                            // Update pinged usernames and ids based on actual mentions in the text
                            setPingedUsernames(activeUsers.map(u => u.vUserName));
                            setPingedUserIds(activeUsers.map(u => u.iUserId));
                        }}




                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {/* Existing attachments from the message being edited */}
                        {isEditing && editMsgIndex !== null &&
                            messages[editMsgIndex]?.attachments?.map((file, idx) => (
                                <Chip
                                    key={`existing-${idx}`}
                                    label={file.original_filename}
                                    onDelete={() => {
                                        const updatedMessages = [...messages];
                                        updatedMessages[editMsgIndex].attachments = updatedMessages[editMsgIndex].attachments.filter((_, i) => i !== idx);
                                        setMessages(updatedMessages);
                                    }}
                                    size="small"
                                    color="secondary"
                                />
                            ))}

                        {/* New attachments added during this session */}
                        {queuedFile.map((file, idx) => (
                            <Chip
                                key={`new-${idx}`}
                                label={file.name}
                                onDelete={() => setQueuedFile((prev) => prev.filter((_, i) => i !== idx))}
                                size="small"
                                color="primary"
                            />
                        ))}
                    </Box>


                </Box>

                {isEditing && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                            setIsEditing(false);
                            setEditMsgIndex(null);
                            setText("");
                            setQueuedFile([]);
                        }}
                    >
                        Cancel
                    </Button>
                )}

                <Button
                    variant="contained"
                    onClick={handleSend}
                    sx={{ minWidth: 80, mb: 2 }}
                    disabled={
                        isEditing
                            ? text.trim() === "" &&
                            queuedFile.length === 0 &&
                            (!messages[editMsgIndex]?.attachments || messages[editMsgIndex]?.attachments?.length === 0)
                            : text.trim() === "" && queuedFile.length === 0
                    }

                >
                    send
                </Button>


            </Box>

            {/* Emoji Picker */}
            {showEmojiPicker && (
                <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
                    <Box sx={{ position: "absolute", bottom: 100, left: 20, zIndex: 10 }}>
                        <Picker onEmojiClick={handleEmojiClick} />
                    </Box>
                </ClickAwayListener>
            )}

            {/* Message Options */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem onClick={handleCopy}>Copy</MenuItem>
                {isCurrentUserMessage && (
                    <>
                        <MenuItem onClick={handleDelete}>Delete</MenuItem>
                        <MenuItem onClick={() => {
                            const msgToEdit = messages[selectedMsgIndex];
                            setText(msgToEdit?.content || "");
                            setQueuedFile([]);
                            setIsEditing(true);
                            setEditMsgIndex(selectedMsgIndex);
                            handleClose();
                        }}>
                            Edit
                        </MenuItem>
                    </>
                )}
            </Menu>
            <Menu
                anchorEl={mentionAnchorEl}
                open={Boolean(mentionAnchorEl) && mentionQuery.length >= 0}
                onClose={() => {
                    setMentionAnchorEl(null);
                    setMentionQuery('');
                }}
            >
                {dynamicStateDropdown
                    ?.filter(user => user.vUserName.toLowerCase().includes(mentionQuery.toLowerCase()))
                    ?.map((user) => (
                        <MenuItem
                            key={user.iUserId}
                            onClick={() => {
                                const cursorPos = text.lastIndexOf('@' + mentionQuery);
                                const before = text.slice(0, cursorPos);
                                const after = text.slice(cursorPos + mentionQuery.length + 1);
                                const updatedText = `${before}@${user.vUserName} ${after}`;

                                setText(updatedText);

                                // Update both usernames & IDs without duplicates
                                setPingedUsernames(prev => [...new Set([...prev, user.vUserName])]);
                                setPingedUserIds(prev => [...new Set([...prev, user.iUserId])]);

                                setMentionAnchorEl(null);
                                setMentionQuery('');
                            }}

                        >
                            {user.vUserName}
                        </MenuItem>
                    ))}
            </Menu>

        </Box>
    );
};

export default ChatWindow;